/**
 * mtx-subscription-pdp.js
 * Self-contained fix for subscription-only PDPs (requires_selling_plan).
 * Regular product pages are completely untouched.
 *
 * - Variant/seat change → id, price (allocation), SKU, URL
 * - Add to cart → clean payload with variant + valid selling_plan only
 * - Never trusts stale Subscriptions-app radios from another seat tier
 */
(function () {
  "use strict";

  var MARKER = "mtx-subscription-pdp:v4";

  function log() {
    // Always log cart diagnostics; set window.MTX_DEBUG=false to silence
    if (window.MTX_DEBUG === false) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[" + MARKER + "]");
    console.log.apply(console, args);
  }

  /**
   * POST JSON via XHR so Umbrella/GTM fetch wrappers cannot rewrite /cart/add.js.
   */
  function postCartAdd(item) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open("POST", "/cart/add.js", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Accept", "application/json");
      xhr.withCredentials = true;
      xhr.onload = function () {
        var text = xhr.responseText || "";
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          data = { description: text };
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          var msg =
            (data && (data.description || data.message)) ||
            "Could not add to cart (HTTP " + xhr.status + ").";
          var err = new Error(msg);
          err.status = xhr.status;
          err.payload = data;
          err.item = item;
          reject(err);
        }
      };
      xhr.onerror = function () {
        var err = new Error("Network error while adding to cart.");
        err.item = item;
        reject(err);
      };
      // items[] is the modern Cart AJAX shape used by the theme CartApi
      xhr.send(JSON.stringify({ items: [item] }));
    });
  }

  function getRoot() {
    return document.querySelector("[data-js-product-container]");
  }

  function getProduct() {
    var el = document.querySelector("[data-js-product-json]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (e) {
      return null;
    }
  }

  function money(cents) {
    if (cents == null || isNaN(cents)) return "";
    var fmt =
      (window.themeCore &&
        window.themeCore.objects &&
        window.themeCore.objects.shop &&
        (window.themeCore.objects.shop.money_with_currency_format ||
          window.themeCore.objects.shop.money_format)) ||
      (window.Shopify && window.Shopify.money_format) ||
      "${{amount}}";
    if (
      window.themeCore &&
      window.themeCore.utils &&
      typeof window.themeCore.utils.formatMoney === "function"
    ) {
      return window.themeCore.utils.formatMoney(cents, fmt);
    }
    var amount = (Number(cents) / 100).toFixed(2);
    if (amount.slice(-3) === ".00") amount = amount.slice(0, -3);
    return fmt
      .replace("{{amount}}", amount)
      .replace("{{amount_no_decimals}}", String(Math.round(cents / 100)));
  }

  function getSelectedOptions(root) {
    var optionsBox = root.querySelector(".js-product-options") || root;
    var selected = {};
    optionsBox.querySelectorAll("input[data-option]:checked, select[data-option]").forEach(function (el) {
      var name = el.getAttribute("data-option");
      if (!name) return;
      selected[name] = el.value;
    });
    return selected;
  }

  function findVariant(product, selectedByName) {
    if (!product || !product.variants) return null;
    var names = (product.options || []).map(function (o) {
      return typeof o === "string" ? o : o && o.name;
    });
    return (
      product.variants.find(function (v) {
        return names.every(function (name, i) {
          var want = selectedByName[name];
          if (want == null) return true;
          return String(v["option" + (i + 1)]) === String(want);
        });
      }) || null
    );
  }

  function variantById(product, id) {
    if (!product || !product.variants || id == null) return null;
    var n = parseInt(id, 10);
    return (
      product.variants.find(function (v) {
        return v.id === n;
      }) || null
    );
  }

  /** Plan id only if it belongs to this variant's allocations */
  function planValidForVariant(variant, planId) {
    if (!variant || !planId) return false;
    var allocs = variant.selling_plan_allocations || [];
    return allocs.some(function (a) {
      return String(a.selling_plan_id) === String(planId);
    });
  }

  function pickAllocation(variant, preferredPlanId) {
    var allocs = (variant && variant.selling_plan_allocations) || [];
    if (!allocs.length) return null;
    if (preferredPlanId && planValidForVariant(variant, preferredPlanId)) {
      return allocs.find(function (a) {
        return String(a.selling_plan_id) === String(preferredPlanId);
      });
    }
    // Prefer a non-zero priced plan (Shopify often rejects $0 subscription adds as "sold out")
    return (
      allocs.find(function (a) {
        return Number(a.price) > 0;
      }) || allocs[0]
    );
  }

  /**
   * Resolve selling plan for the *current* variant only.
   * Ignore checked radios that belong to another seat tier.
   */
  function resolvePlanId(root, form, variant) {
    if (!variant) return null;
    var candidates = [];

    // 1) Hidden input we control (set on last applyVariant)
    var hidden =
      (form && form.querySelector('input[name="selling_plan"]:not([type="radio"])')) ||
      (root && root.querySelector('input[name="selling_plan"]:not([type="radio"])'));
    if (hidden && hidden.value) candidates.push(String(hidden.value));

    // 2) Checked radios (Subscriptions app) — only if valid for this variant
    var radios = document.querySelectorAll('input[type="radio"][name="selling_plan"]:checked');
    radios.forEach(function (r) {
      if (r.value) candidates.push(String(r.value));
    });

    for (var i = 0; i < candidates.length; i++) {
      if (planValidForVariant(variant, candidates[i])) return candidates[i];
    }

    var alloc = pickAllocation(variant, null);
    return alloc && alloc.selling_plan_id != null ? String(alloc.selling_plan_id) : null;
  }

  function setSellingPlan(form, planId) {
    if (!form) return;
    var hidden = form.querySelector('input[name="selling_plan"]:not([type="radio"])');
    if (!hidden) {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "selling_plan";
      form.appendChild(hidden);
    }
    if (planId) {
      hidden.value = String(planId);
      hidden.disabled = false;
      hidden.removeAttribute("disabled");
    } else {
      hidden.value = "";
      hidden.disabled = true;
    }
  }

  function setVariantId(root, form, variantId) {
    var id = String(variantId);
    root.querySelectorAll('input[name="id"], select[name="id"]').forEach(function (el) {
      el.value = id;
    });
    if (form) {
      form.querySelectorAll('input[name="id"], select[name="id"]').forEach(function (el) {
        el.value = id;
      });
      if (form.id) {
        document.querySelectorAll('input[name="id"][form="' + form.id + '"]').forEach(function (el) {
          el.value = id;
        });
      }
    }
  }

  function updatePriceDom(root, price, compare) {
    var onSale = compare != null && Number(compare) > Number(price);
    var moneyPrice = money(price);
    var moneyCompare = compare != null ? money(compare) : "";
    function paint(scope) {
      scope.querySelectorAll("[data-product-price]").forEach(function (el) {
        el.innerHTML = moneyPrice;
        el.setAttribute("data-amount", String(price));
      });
      scope.querySelectorAll("[data-price-old]").forEach(function (el) {
        el.innerHTML = moneyCompare;
        if (compare != null) el.setAttribute("data-amount", String(compare));
      });
      scope.querySelectorAll(".js-price").forEach(function (el) {
        el.classList.toggle("price--on-sale", !!onSale);
      });
    }
    paint(root);
    document.querySelectorAll(".js-sticky-add-to-cart, .product-sticky-add-to-cart").forEach(paint);
  }

  function updateSkuDom(root, variant) {
    var sku = variant && variant.sku ? String(variant.sku).trim() : "";
    root.querySelectorAll(".js-product-sku").forEach(function (el) {
      if (!sku) {
        el.innerHTML = "";
        return;
      }
      var template = el.getAttribute("data-sku-text") || "";
      el.innerHTML = template
        ? template.split("{SKU}").join(sku).split("{sku}").join(sku)
        : sku;
    });
  }

  function updateHistory(variantId) {
    try {
      if (window.themeCore && window.themeCore.utils && window.themeCore.utils.getUrlWithVariant) {
        var url = window.themeCore.utils.getUrlWithVariant(window.location.href, variantId);
        window.history.replaceState({ path: url }, "", url);
        return;
      }
    } catch (e) {}
    try {
      var u = new URL(window.location.href);
      u.searchParams.set("variant", String(variantId));
      window.history.replaceState({ path: u.toString() }, "", u.toString());
    } catch (e2) {}
  }

  function applyVariant(product, root, form, variant) {
    if (!variant) return;
    var planId = resolvePlanId(root, form, variant);
    setVariantId(root, form, variant.id);
    setSellingPlan(form, planId);
    var allocation = pickAllocation(variant, planId);
    var price = allocation ? allocation.price : variant.price;
    var compare =
      allocation && allocation.compare_at_price != null
        ? allocation.compare_at_price
        : variant.compare_at_price;
    updatePriceDom(root, price, compare);
    updateSkuDom(root, variant);
    updateHistory(variant.id);
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: { variant: variant, variantId: variant.id, sellingPlanId: planId }
      })
    );
    log("applied", variant.id, variant.title, "sku", variant.sku, "plan", planId, "price", price);
  }

  function currentVariant(product, root) {
    var selected = getSelectedOptions(root);
    var v = findVariant(product, selected);
    if (v) return v;
    var idEl = root.querySelector('[name="id"]');
    if (idEl && idEl.value) return variantById(product, idEl.value);
    return product.selected_or_first_available_variant
      ? variantById(product, product.selected_or_first_available_variant.id)
      : product.variants && product.variants[0];
  }

  function showError(root, form, message) {
    var box =
      (form && form.querySelector(".js-product-quantity-error")) ||
      root.querySelector(".js-product-quantity-error") ||
      document.querySelector(".js-product-quantity-error");
    if (box) {
      box.textContent = message;
      box.classList.remove("is-hidden");
      var wrap = box.closest(".product-add-to-cart__error, .is-hidden");
      if (wrap) wrap.classList.remove("is-hidden");
    } else if (window.themeCore && window.themeCore.CartNotificationError) {
      try {
        window.themeCore.CartNotificationError.addNotification(message);
        window.themeCore.CartNotificationError.open();
      } catch (e) {
        console.error(message);
      }
    } else {
      console.error(message);
      alert(message);
    }
  }

  function clearError(root, form) {
    var box =
      (form && form.querySelector(".js-product-quantity-error")) ||
      root.querySelector(".js-product-quantity-error");
    if (box) box.textContent = "";
  }

  function setLoading(form, on) {
    if (!form) return;
    form.querySelectorAll('[type="submit"], .js-sticky-add-to-cart-button').forEach(function (btn) {
      btn.classList.toggle("is-loading", !!on);
      if (on) btn.setAttribute("aria-busy", "true");
      else btn.removeAttribute("aria-busy");
    });
    document.querySelectorAll(".js-sticky-add-to-cart-button").forEach(function (btn) {
      btn.classList.toggle("is-loading", !!on);
    });
  }

  function addToCart(product, root, form) {
    var variant = currentVariant(product, root);
    if (!variant) {
      showError(root, form, "Please select a valid option.");
      return Promise.reject(new Error("no variant"));
    }

    // Always re-resolve plan against THIS variant's allocations only
    var planId = resolvePlanId(root, form, variant);
    if (!planId) {
      var forced = pickAllocation(variant, null);
      planId = forced && forced.selling_plan_id != null ? String(forced.selling_plan_id) : null;
    }
    if (!planValidForVariant(variant, planId)) {
      var fallback = pickAllocation(variant, null);
      planId = fallback && fallback.selling_plan_id != null ? String(fallback.selling_plan_id) : null;
    }

    setVariantId(root, form, variant.id);
    setSellingPlan(form, planId);

    if (!planId) {
      showError(
        root,
        form,
        "Please select a subscription plan (monthly or yearly) before adding to cart."
      );
      return Promise.reject(new Error("no selling plan"));
    }

    var alloc = pickAllocation(variant, planId);
    var qty = 1;
    var qtyInput =
      (form && form.querySelector('[name="quantity"]')) ||
      root.querySelector('[name="quantity"], [data-quantity-input]');
    if (qtyInput && qtyInput.value) {
      var q = parseInt(qtyInput.value, 10);
      if (!isNaN(q) && q > 0) qty = q;
    }

    var item = {
      id: parseInt(variant.id, 10),
      quantity: qty,
      selling_plan: parseInt(planId, 10)
    };

    // Sanity: plan must belong to this variant
    if (!planValidForVariant(variant, item.selling_plan)) {
      showError(
        root,
        form,
        "Subscription plan does not match the selected seat. Change seat or plan and try again."
      );
      return Promise.reject(new Error("plan/variant mismatch"));
    }

    log("cart/add.js payload", item, "variant", variant.title, "sku", variant.sku, "allocPrice", alloc && alloc.price);

    clearError(root, form);
    setLoading(form, true);

    return postCartAdd(item)
      .then(function (data) {
        log("cart success", data);
        if (window.themeCore && window.themeCore.CartApi) {
          return window.themeCore.CartApi.makeRequest(
            window.themeCore.CartApi.actions.GET_CART
          ).then(function () {
            return data;
          });
        }
        document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
        return data;
      })
      .catch(function (err) {
        console.error("[" + MARKER + "] add failed", err && err.item, err && err.payload || err);
        var msg = (err && err.message) || "Could not add to cart.";
        if (/sold out/i.test(msg)) {
          msg =
            msg +
            " [debug: id=" +
            item.id +
            " plan=" +
            item.selling_plan +
            " seat=" +
            (variant.title || "") +
            " allocPrice=" +
            (alloc ? alloc.price : "?") +
            "]. If the seat shows a price above, check Admin → ApexNest → Purchase options: each seat needs its own active selling plan with a non-zero price, and the plan must be assigned to that variant.";
        }
        showError(root, form, msg);
        throw err;
      })
      .finally(function () {
        setLoading(form, false);
      });
  }

  function bindSubmit(product, root, form) {
    if (!form || form._mtxSubBound) return;
    form._mtxSubBound = true;

    function handle(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }
      addToCart(product, root, form);
    }

    // Capture phase on form submit
    form.addEventListener("submit", handle, true);

    // All ATC buttons inside the product (including ones that don't bubble submit)
    root.addEventListener(
      "click",
      function (e) {
        var btn = e.target && e.target.closest && e.target.closest(
          'button[type="submit"], .product-add-to-cart__button, .add-to-cart, [name="add"]'
        );
        if (!btn) return;
        // Only product form submits
        if (btn.form && btn.form !== form && btn.getAttribute("form") !== form.id) return;
        if (!btn.form && btn.getAttribute("form") && btn.getAttribute("form") !== form.id) return;
        if (!root.contains(btn) && btn.getAttribute("form") !== form.id) return;
        handle(e);
      },
      true
    );

    // Sticky ATC
    document.addEventListener(
      "click",
      function (e) {
        var btn = e.target && e.target.closest && e.target.closest(".js-sticky-add-to-cart-button");
        if (!btn) return;
        var formId = btn.getAttribute("form");
        if (formId && formId === form.id) handle(e);
      },
      true
    );
  }

  function init() {
    var root = getRoot();
    if (!root || root._mtxSubPdpBound) return;
    var product = getProduct();
    if (!product || !product.requires_selling_plan) {
      log("skip — not subscription-required");
      return;
    }
    var form =
      root.querySelector("form[data-js-product-form]") ||
      root.querySelector('form[action*="/cart/add"]') ||
      document.querySelector("form[data-js-product-form]");
    if (!form) {
      log("no form");
      return;
    }

    root._mtxSubPdpBound = true;
    log("active", product.handle || product.id);

    root.addEventListener(
      "change",
      function (e) {
        var t = e.target;
        if (!t) return;
        if (t.hasAttribute && t.hasAttribute("data-option")) {
          var v = findVariant(product, getSelectedOptions(root));
          if (v) applyVariant(product, root, form, v);
          return;
        }
        if (t.getAttribute && t.getAttribute("name") === "selling_plan") {
          var cur = currentVariant(product, root);
          if (cur) applyVariant(product, root, form, cur);
        }
      },
      true
    );

    document.addEventListener("change", function (e) {
      var t = e.target;
      if (!t || !t.getAttribute) return;
      if (t.getAttribute("name") !== "selling_plan") return;
      if (!root.contains(t) && t.getAttribute("form") !== form.id) return;
      var cur = currentVariant(product, root);
      if (cur) applyVariant(product, root, form, cur);
    });

    bindSubmit(product, root, form);

    var initial = currentVariant(product, root);
    if (initial) applyVariant(product, root, form, initial);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("theme:all:loaded", init, { once: true });
  window.setTimeout(init, 800);
})();
